import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, careTypeLabel } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Heart, Search, Sparkles } from "lucide-react";

export default function CaregiverDashboard() {
  const [items, setItems] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: inq }, { data: pl }] = await Promise.all([
          api.get("/inquiries/mine"),
          api.get("/placement-requests/mine"),
        ]);
        setItems(inq.items || []);
        setPlacements(pl.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="caregiver-dashboard" className="mx-auto max-w-5xl px-6 py-10 lg:px-8">
      <p className="text-sm uppercase tracking-[0.18em] text-primary">Caregiver dashboard</p>
      <h1 className="mt-2 font-serif text-4xl text-forest sm:text-5xl">Your inquiries</h1>
      <p className="mt-2 text-muted-foreground">
        Drop-in requests, respite asks, and trial visits — all in one calm list.
      </p>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild className="rounded-full bg-primary text-primary-foreground" data-testid="cg-find-care-btn">
          <Link to="/search"><Search className="mr-2 h-4 w-4" /> Find more centers</Link>
        </Button>
        <Button asChild variant="outline" className="rounded-full border-gold/60 text-forest hover:bg-gold/10" data-testid="cg-placement-help-btn">
          <Link to="/placement-help"><Sparkles className="mr-2 h-4 w-4 text-gold" /> Senior placement help</Link>
        </Button>
      </div>

      {placements.length > 0 && (
        <div className="mt-10">
          <h2 className="font-serif text-2xl text-forest">Your placement requests</h2>
          <p className="text-sm text-muted-foreground">
            A Providence Partners advisor will reach out personally.
          </p>
          <div className="mt-4 grid gap-3">
            {placements.map((p) => (
              <Card key={p.id} data-testid={`cg-placement-${p.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gold/40 bg-gold/5 p-5">
                <div>
                  <h3 className="font-serif text-lg text-forest">
                    For {p.loved_one_name || "(your loved one)"}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    Priority: {p.care_priority} · Area: {p.preferred_area || "—"} · Timeline: {p.timeline}
                  </p>
                </div>
                <Badge variant="outline" className="rounded-full capitalize">{p.status}</Badge>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 grid gap-4">
        {loading && <p className="text-muted-foreground">Loading…</p>}
        {!loading && items.length === 0 && (
          <Card className="rounded-2xl border border-dashed border-border p-10 text-center">
            <Heart className="mx-auto h-8 w-8 text-primary" />
            <p className="mt-3 font-serif text-2xl text-forest">No inquiries yet</p>
            <p className="mt-2 text-muted-foreground">When you reach out to a member home, you'll see it tracked here.</p>
            <Button asChild className="mt-5 rounded-full bg-primary text-primary-foreground">
              <Link to="/search">Search nearby homes</Link>
            </Button>
          </Card>
        )}
        {items.map((i) => (
          <Card key={i.id} data-testid={`cg-inquiry-${i.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card p-5">
            <div>
              <h3 className="font-serif text-xl text-forest">{i.facility_name}</h3>
              <p className="text-sm text-muted-foreground">
                {careTypeLabel(i.care_type)}{i.requested_date ? ` · ${i.requested_date}` : ""}
              </p>
              {i.loved_one_name && (
                <p className="mt-1 text-xs text-muted-foreground">For: {i.loved_one_name}</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="rounded-full capitalize">{i.status}</Badge>
              <Button asChild size="sm" variant="outline" className="rounded-full">
                <Link to={`/facility/${i.facility_id}`}>View facility</Link>
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
